<template>
   <Navbar />

   <slot></slot>

   <LazyHydrate when-visible>
      <AppFooter v-if="!hideFooter" />
   </LazyHydrate>

   <FlashMessages v-if="flash?.success || flash?.error" />

   <Wizard v-if="showWizard" />

   <Comparison v-if="comparisonList" />

   <CookieBanner v-if="!cookieConsentId" />

   <teleport to="body">
      <IntercomButton v-if="showChat" />
   </teleport>
</template>

<script setup>
import { usePage } from '@inertiajs/vue3'
import { computed, defineAsyncComponent, onMounted, onUnmounted, ref } from 'vue'

// Import components directly for critical components
import AppFooter from '@/Layouts/Footer.vue'
import Navbar from '@/Layouts/Navbar.vue'
const inBrowser = typeof window !== 'undefined'

// Import IntercomButton
const IntercomButton = defineAsyncComponent(() => import('@/Components/IntercomButton.vue'))

// Move cookieConsentId to computed property with browser check
const cookieConsentId = computed(() => (inBrowser ? window.cookieConsentId : 'fake-cookie-consent-id'))

// Lazy load non-critical components
const CookieBanner = defineAsyncComponent(() => import('@/Components/CookieBanner.vue'))
const Comparison = defineAsyncComponent(() => import('@/Components/ComparisonList.vue'))
const FlashMessages = defineAsyncComponent(() => import('@/Components/FlashMessages.vue'))
const Wizard = defineAsyncComponent(() => import('@/Components/Wizard.vue'))
const showChat = computed(() => {
   return inBrowser && !window.Intercom && !localStorage.getItem('chatStarted')
})
// Reactive state
const showWizard = ref(false)
const loaded = ref(false)

// Page props
const comparisonList = computed(() => usePage().props.comparisonList)
const flash = computed(() => usePage().props.flash)
const hideFooter = computed(() => usePage().props.active.checkout)
const user = computed(() => usePage().props.user)

defineProps({
   withoutContainer: Boolean,
})

onMounted(() => {
   // Setup event listeners
   setupEventListeners()
   loaded.value = true

   if (inBrowser) {
      window.intercomSettings = {
         api_base: 'https://api-iam.intercom.io',
         app_id: 'i2p3nmsz',
         ...(user.value && {
            user_id: user.value.id,
            name: `${user.value.first_name} ${user.value.last_name}`,
            email: user.value.email,
            created_at: user.value.created_at,
         }),
      }
   }

   if (localStorage.getItem('chatStarted')) {
      // localStorage.removeItem('chatStarted')
      loadIntercom()
   }
})

onUnmounted(() => {
   if (!inBrowser) return
   window.mitt.off('open-wizard', handleOpenWizard)
})

function handleOpenWizard() {
   if (!inBrowser) return
   showWizard.value = true
   document.body.classList.add('modal-open')
}

// Setup functions
function setupEventListeners() {
   if (!inBrowser) return

   window.mitt.on('open-wizard', handleOpenWizard)
}

const loadIntercom = () => {
   if (!inBrowser) return

   const w = window
   const d = document
   const i = function () {
      i.c(arguments)
   }
   i.q = []
   i.c = function (args) {
      i.q.push(args)
   }
   w.Intercom = i

   const s = d.createElement('script')
   s.type = 'text/javascript'
   s.async = true
   s.src = 'https://widget.intercom.io/widget/i2p3nmsz'
   const x = d.getElementsByTagName('script')[0]
   x.parentNode.insertBefore(s, x)
}
</script>
